
import Vue from "vue";
import request from "@/utils/request";
import Breadcrump from "@/components/common/Breadcrump.vue";

interface FileInfo {
  fullName: string;
  size: number;
  lastModified: string;
  downloadLink: string;
}

interface DeviceOTAFormData {
  version: string;
  updateTitle: string;
  updateDesc: string;
  forceUpdate: boolean;
  appFile: File | null;
  batchFile: File | null;
  resourceFile: File | null;
  deleteApp: boolean;
  deleteBatch: boolean;
  deleteResource: boolean;
  isActive: boolean;
}

interface PreviousOTAResponse {
  lastVersion: string;
  updateTitle: string;
  updateDesc: string;
  lastActiveVersion: string;
  forceUpdate: boolean;
  isActiveUpdate: boolean;
  appFileInfo?: FileInfo;
  batchFileInfo?: FileInfo;
  resourceFileInfo?: FileInfo;
}

interface VFormInstance {
  validate(): boolean;
  resetValidation(): void;
}

export default Vue.extend({
  components: { Breadcrump },
  props: {
    id: {
      type: [String, Number],
      default: null
    }
  },

  data() {
    return {
      valid: true as boolean,
      loading: false as boolean,
      uploadProgress: 0 as number,
      lastVersion: "" as string,
      lastActiveVersion: "" as string,
      appFileInfoString: "" as string,
      batchFileInfoString: "" as string,
      resourceFileInfoString: "" as string,
      originalAppFileInfo: undefined as FileInfo | undefined,
      originalBatchFileInfo: undefined as FileInfo | undefined,
      originalResourceFileInfo: undefined as FileInfo | undefined,
      errors: [] as string[],
      showConfirmDialog: false as boolean,
      form: {
        version: "",
        forceUpdate: false,
        isActiveUpdate: true,
        updateTitle: "",
        updateDesc: "",
        appFile: null,
        batchFile: null,
        resourceFile: null,
        deleteApp: false,
        deleteBatch: false,
        deleteResource: false,
        isActive: false
      } as DeviceOTAFormData
    };
  },

  computed: {
    isEdit(): boolean {
      return !!this.id && this.id !== "add";
    },
    mdSize(): number {
      return !!this.id && this.id !== "add" ? 8 : 12;
    },
    crumbs(): any[] {
      const base = [
        { text: "خانه", href: "/", disabled: false },
        { text: "نسخه‌های دستگاه", href: "/device-ota", disabled: false }
      ];
      return this.isEdit
        ? [...base, { text: `${this.form.updateTitle || ""}`, disabled: true }]
        : [...base, { text: "افزودن نسخه جدید", disabled: true }];
    }
  },

  mounted() {
    if (this.isEdit) {
      this.fetchLatestVersion();
    }
  },

  methods: {
    formatFileSize(bytes: number): string {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },

    formatFileInfo(fileInfo?: FileInfo): string {
      if (!fileInfo) return "";
      return `${fileInfo.fullName} (${this.formatFileSize(fileInfo.size)}) - ${
        fileInfo.lastModified
      }`;
    },
    async downloadFile(fileInfo) {
      if (!fileInfo || !fileInfo.downloadLink) return;

      try {
        const response = await fetch(fileInfo.downloadLink);

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileInfo.fullName || "download";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch {
        window.open(fileInfo.downloadLink, "_blank");
      }
    },

    toggleDelete(type: "app" | "batch" | "resource"): void {
      if (type === "app") {
        this.form.deleteApp = !this.form.deleteApp;
        this.appFileInfoString = this.form.deleteApp
          ? ""
          : this.formatFileInfo(this.originalAppFileInfo);
      } else if (type === "batch") {
        this.form.deleteBatch = !this.form.deleteBatch;
        this.batchFileInfoString = this.form.deleteBatch
          ? ""
          : this.formatFileInfo(this.originalBatchFileInfo);
      } else if (type === "resource") {
        this.form.deleteResource = !this.form.deleteResource;
        this.resourceFileInfoString = this.form.deleteResource
          ? ""
          : this.formatFileInfo(this.originalResourceFileInfo);
      }
    },

    async fetchLatestVersion(): Promise<void> {
      try {
        const res = await request.get(`/DeviceOTAList/${this.id}/latest`);
        const data: PreviousOTAResponse = res.data?.result || {};
        this.lastVersion = data.lastVersion || "";
        this.lastActiveVersion = data.lastActiveVersion || "";
        this.form.forceUpdate = data.forceUpdate;
        this.form.isActive = data.isActiveUpdate;
        this.form.updateTitle = data.updateTitle || "";
        this.form.updateDesc = data.updateDesc || "";
        this.originalAppFileInfo = data.appFileInfo;
        this.originalBatchFileInfo = data.batchFileInfo;
        this.originalResourceFileInfo = data.resourceFileInfo;
        this.appFileInfoString = this.formatFileInfo(data.appFileInfo);
        this.batchFileInfoString = this.formatFileInfo(data.batchFileInfo);
        this.resourceFileInfoString = this.formatFileInfo(
          data.resourceFileInfo
        );
        this.form.version = this.lastVersion;
      } catch {
        this.form.version = "";
      }
    },

    async submitForm(): Promise<void> {
      this.errors = [];
      this.uploadProgress = 0;

      if (!this.form.version) this.errors.push("شماره نسخه الزامی است");
      if (this.form.version === this.lastVersion && !this.isEdit) {
        this.errors.push("نسخه جدید نباید با آخرین نسخه یکسان باشد");
      }

      const hasFile =
        this.form.appFile || this.form.batchFile || this.form.resourceFile;
      const hasDelete =
        this.form.deleteApp ||
        this.form.deleteBatch ||
        this.form.deleteResource;
      if (!hasFile && !this.isEdit) {
        this.errors.push("وجود حداقل یک فایل برای ساخت نسخه جدید الزامی است");
      }

      const formRef = (this.$refs.form as unknown) as VFormInstance;
      if (!formRef.validate() || this.errors.length > 0) return;

      if (hasDelete) {
        this.showConfirmDialog = true;
        return;
      }

      await this.proceedWithUpload();
    },

    async proceedWithSubmit(): Promise<void> {
      this.showConfirmDialog = false;
      await this.proceedWithUpload();
    },

    async proceedWithUpload(): Promise<void> {
      this.loading = true;
      try {
        const formData = new FormData();
        formData.append("Version", this.form.version);
        formData.append("ForceUpdate", String(this.form.forceUpdate));
        formData.append("DeleteApp", String(this.form.deleteApp));
        formData.append("IsActive", String(this.form.isActive));
        formData.append("UpdateTitle", String(this.form.updateTitle));
        formData.append("UpdateDesc", String(this.form.updateDesc));
        formData.append("DeleteBatch", String(this.form.deleteBatch));
        formData.append("DeleteResource", String(this.form.deleteResource));
        if (this.form.appFile) formData.append("AppFile", this.form.appFile);
        if (this.form.batchFile)
          formData.append("BatchFile", this.form.batchFile);
        if (this.form.resourceFile)
          formData.append("ResourceFile", this.form.resourceFile);
        if (this.isEdit) formData.append("Id", String(this.id));

        const url = `/DeviceOTAList/upload-files`;
        await request.post(url, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 300000,
          onUploadProgress: (e: any) => {
            if (e.total) this.uploadProgress = (e.loaded / e.total) * 100;
          }
        });

        this.$router.push("/device-ota");
      } catch (err) {
        const msg = err.message || "";
        if (msg.includes("timeout"))
          this.errors.push("آپلود به دلیل timeout متوقف شد");
        else if (msg.includes("413"))
          this.errors.push("حجم فایل بیش از حد مجاز است");
        else this.errors.push("خطا در آپلود: " + msg);
      } finally {
        this.loading = false;
        this.uploadProgress = 0;
      }
    }
  }
});
